import { AD_BREAK_TYPES } from "../constants/fast";

import type { Range, VariantTimeslot } from "okast-timeslot";
import type Timeslot from "okast-timeslot";

import type {
  Schedule,
  ScheduleItemWithScheduleId,
} from "../api/FastAPI.types";
import type {
  InterstitialAdbreaks,
  SchedulingTimeslotItem,
} from "../components/fast_channels/timeline/timeline.types";
import type { ScheduleItemObjectType, TypeIndexMedia } from "../constants/fast";
import type {
  AdBreakFormat,
  MediaFormat,
  MediaFormatWithAdBreak,
  TIMESLOT,
} from "../constants/media";
import type {
  ScheduleItemsWithClusters,
  ScheduleItemsWithVariantInfo,
} from "../utils/timelineClusters";

export interface MediaTimeslot {
  uuid: string;
  name: string;
  color: string;
}
export interface Media {
  uuid: string;
  userId: string;
  label: string;
  type: ScheduleItemObjectType;
  startTime: string;
  initialStartTime: string;
  endTime: string;
  mediaId: string;
  duration: number;
  format: MediaFormatWithAdBreak | null;
  scheduleId: string;
  isFirstVariantMedia: boolean | null;
  variantMediaList: ScheduleItemWithScheduleId[];
  nextMediaId?: string;
  isOnBaseline: boolean;
  timeslot?: MediaTimeslot;
  interstitialAdbreaks?: InterstitialAdbreaks;
  itemIdList?: Set<string>;
  updatedAt: string;
  poster: string | null;
}

export interface Kpi {
  objective?: number;
  completion?: number;
  duration?: number;
}

export interface ChannelSettingsFieldValues {
  channelDeliveryName: string;
  channelDescription: string;
  channelShortDescription: string;
  channelRating: { label?: string; value: string };
  channelDeliveryRegions: { value: string; label?: string }[];
  channelGenres: string[];
  channelName: string;
  productionLanguage: string;
  timezone: string;
  firstDayOfWeek: { label?: string; value: string };
  firstHourOfDay: { label?: string; value: string };
  mediaColors: Record<string, string>;
  channelLogoUrl?: string;
  channelLogoFile?: File;
  channelLogoWidth?: number;
  channelLogoHeight?: number;
  channelLogoPosition?: {
    value?: "top_left" | "top_right" | "bottom_left" | "bottom_right";
    label?: string;
  };
  channelLogoAspectRatio?: number;
}

export type SchedulesSettingsFieldValues = Record<
  string,
  Schedule & {
    broadcastSelectList: {
      value: string;
      label: string;
      logoUrl: string;
    }[];
  }
>;

export interface MediaLibraryDragItem {
  uuid: string;
  label: string;
  mediaId: string;
  duration: number;
  formatKey?: MediaFormatWithAdBreak;
  type: TypeIndexMedia | typeof TIMESLOT;
  color?: string;
  cuepoints?: Cuepoints;
  poster?: string | null;
}

export type GenericDropzoneMedia<
  T extends MediaFormatWithAdBreak | AdBreakFormat,
> = Omit<MediaLibraryDragItem, "formatKey"> & {
  uuid: string;
  rank: number;
  formatKey?: T;
};

export type DropzoneMedia = GenericDropzoneMedia<
  MediaFormat | MediaFormatWithAdBreak | AdBreakFormat
>;

export type PlaylistDropzoneMedia = GenericDropzoneMedia<MediaFormat>;

export type AdBreakDropzoneMedia = GenericDropzoneMedia<AdBreakFormat>;

const { ADPODS, PRE_ADS_BUMPER, POST_ADS_BUMPER } = AD_BREAK_TYPES;

export interface AdBreakListByType {
  [ADPODS]: {
    mediaList: AdBreakDropzoneMedia[];
    duration: number;
  };
  [PRE_ADS_BUMPER]: {
    mediaList: AdBreakDropzoneMedia[];
    duration: number;
  };
  [POST_ADS_BUMPER]: {
    mediaList: AdBreakDropzoneMedia[];
    duration: number;
  };
}

export interface ParametersDataWorker {
  scheduleItemList: ScheduleItemsWithClusters | ScheduleItemsWithVariantInfo[];
  previousCurrentPeriodMediaList: Media[];
  scheduleList: Schedule[];
  type: "scheduling_timeline";
  range: Range;
  currentDay: string;
  isClustered: boolean;
  messageIdList: string[];
}

export interface TimelineContentData {
  timeslot: Timeslot;
  variantTimeslots: VariantTimeslot[];
  timelineContent: Record<string, Media>;
  mediaIdListOnCurrentDay: string[];
  mediaIdListOnNextDay: string[];
  mediaIdListOnPreviousDay: string[];
  currentDay: string;
  previousDay: string;
  nextDay: string;
  isClustered: boolean;
  messageIdList: string[];
  type: "scheduling_timeline";
}

export interface DropzoneMoveItemParams {
  itemUuid: string;
  newRank: number;
  newItem?: {
    uuid: string;
    rank: number;
  };
}

export enum TimeslotColumns {
  Rank = "rank",
  Mode = "mode",
  Source = "source",
  Count = "count",
  StartIndex = "startIndex",
  Increment = "increment",
  Ads = "ads",
  Delete = "delete",
}

export interface TimeslotRuleSelectOptions {
  [TimeslotColumns.Count]: { label: number; value: number }[];
  [TimeslotColumns.StartIndex]: { label: string; value: number }[];
  [TimeslotColumns.Increment]: { label: number; value: number }[];
}

export type TimeslotRuleCountOptions =
  TimeslotRuleSelectOptions[TimeslotColumns.Count];
export type TimeslotRuleStartIndexOptions =
  TimeslotRuleSelectOptions[TimeslotColumns.StartIndex];
export type TimeslotRuleIncrementOptions =
  TimeslotRuleSelectOptions[TimeslotColumns.Increment];

export interface AccSchedulingTimeslotItems {
  timeslotList: SchedulingTimeslotItem[];
  currentTemplate?: SchedulingTimeslotItem;
}
export interface TimelineLoopPreviewMedia {
  startTime: number;
  endTime: number;
  dropzoneId: string;
}

export interface Timebounds {
  startTime: number;
  endTime: number;
}

export enum TimeboundKeys {
  StartTime = "startTime",
  EndTime = "endTime",
}

interface BreadcrumbsItem {
  name: string;
  url?: string;
}

export type Breadcrumbs = BreadcrumbsItem[];

export interface TimeslotByStartDate {
  timeslot: Timeslot;
  variantTimeslots: VariantTimeslot[];
}

export enum ComputedTimelineItemsType {
  WITH_CLUSTER = "withCluster",
  WITHOUT_CLUSTER = "withoutCluster",
}
export type Cuepoints = number[];

export type TimelineMediaIdListByStartDate = Record<
  string,
  {
    [ComputedTimelineItemsType.WITHOUT_CLUSTER]?: string[];
    [ComputedTimelineItemsType.WITH_CLUSTER]?: string[];
  }
>;

export type TimeslotsByStartDate = Record<
  string,
  {
    [ComputedTimelineItemsType.WITHOUT_CLUSTER]?: TimeslotByStartDate;
    [ComputedTimelineItemsType.WITH_CLUSTER]?: TimeslotByStartDate;
  }
>;

export type TimelineDataPartialFetched = Record<
  string,
  {
    [ComputedTimelineItemsType.WITHOUT_CLUSTER]?: boolean;
    [ComputedTimelineItemsType.WITH_CLUSTER]?: boolean;
  }
>;

export type TimelineContent = Record<string, Media>;

export interface TimelineItems {
  timelineMediaIdListByStartDate: TimelineMediaIdListByStartDate;
  timeslotsByStartDate: TimeslotsByStartDate;
  timelineDataPartialFetched: TimelineDataPartialFetched;
  timelineContent: TimelineContent;
}

export enum CalendarUrlParams {
  Date = "date",
  View = "view",
}

export type CalendarView = "week" | "month";
